import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { CachedCatalogItem, CachedHistory, CachedItem, CachedRequisition, OutboxOperation, SyncMetadata } from './types'
import { OFFLINE_DB_NAME, OFFLINE_DB_VERSION } from './types'

interface OfflineSchema extends DBSchema {
  cache_requisicoes: {
    key: string
    value: CachedRequisition
    indexes: { by_user: string; by_user_cached: [string, string] }
  }
  cache_itens: {
    key: string
    value: CachedItem
    indexes: { by_user: string; by_user_requisicao: [string, string] }
  }
  cache_historico: {
    key: string
    value: CachedHistory
    indexes: { by_user: string; by_user_requisicao: [string, string] }
  }
  cache_catalogo_itens: {
    key: string
    value: CachedCatalogItem
    indexes: { by_user: string; by_user_code: [string, string]; by_user_cached: [string, string] }
  }
  outbox: {
    key: string
    value: OutboxOperation
    indexes: { by_user: string; by_user_status: [string, string]; by_user_created: [string, string]; by_user_entity: [string, string, string] }
  }
  sync_metadata: {
    key: string
    value: SyncMetadata
    indexes: { by_user: string }
  }
}

let databasePromise: Promise<IDBPDatabase<OfflineSchema>> | null = null

export function offlineDb(): Promise<IDBPDatabase<OfflineSchema>> {
  if (!databasePromise) {
    databasePromise = openDB<OfflineSchema>(OFFLINE_DB_NAME, OFFLINE_DB_VERSION, {
      upgrade(database, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
          const requests = database.createObjectStore('cache_requisicoes', { keyPath: 'key' })
          requests.createIndex('by_user', 'userId')
          requests.createIndex('by_user_cached', ['userId', 'cachedAt'])

          const items = database.createObjectStore('cache_itens', { keyPath: 'key' })
          items.createIndex('by_user', 'userId')
          items.createIndex('by_user_requisicao', ['userId', 'requisicaoId'])

          const history = database.createObjectStore('cache_historico', { keyPath: 'key' })
          history.createIndex('by_user', 'userId')
          history.createIndex('by_user_requisicao', ['userId', 'requisicaoId'])

          const outbox = database.createObjectStore('outbox', { keyPath: 'operationId' })
          outbox.createIndex('by_user', 'userId')
          outbox.createIndex('by_user_status', ['userId', 'status'])
          outbox.createIndex('by_user_created', ['userId', 'createdAt'])

          const metadata = database.createObjectStore('sync_metadata', { keyPath: 'key' })
          metadata.createIndex('by_user', 'userId')
        }
        if (oldVersion < 2) {
          const outbox = transaction.objectStore('outbox')
          if (!outbox.indexNames.contains('by_user_entity')) {
            outbox.createIndex('by_user_entity', ['userId', 'entityType', 'entityId'])
          }
        }
        if (oldVersion < 3) {
          const catalog = database.createObjectStore('cache_catalogo_itens', { keyPath: 'key' })
          catalog.createIndex('by_user', 'userId')
          catalog.createIndex('by_user_code', ['userId', 'row.codigo_normalizado'])
          catalog.createIndex('by_user_cached', ['userId', 'cachedAt'])
        }
      },
    })
  }
  return databasePromise
}

export function scopedKey(userId: string, entityId: string): string {
  return `${userId}:${entityId}`
}

export async function resetOfflineDbForTests(): Promise<void> {
  const db = await offlineDb()
  db.close()
  databasePromise = null
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(OFFLINE_DB_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
    request.onblocked = () => reject(new Error('Banco offline bloqueado durante reset de teste.'))
  })
}
